/**
 * CTACard Component
 * Displays and manages a single Call to Action with QR code generation
 */

import React, { useState, useEffect } from "react";
import PromoCodeLookupStatus from "../common/PromoCodeLookupStatus";
import {
  QrCodeIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import {
  useUpdateEventCTA,
  useDeleteEventCTA,
  useGenerateCTAQRCode,
} from "../../hooks/useEventCTAs";
import {
  buildCTAUrl,
  ctaOmitsDiscount,
  getEventCTABaseVariant,
} from "../../services/eventCTA.service";
import { INSURANCE_NO_VOUCHER_NOTE } from "../../services/ctaClassroom";
import type {
  Event,
  EventCallToAction,
  RewardType,
} from "../../types/event.types";
import { SUBSCRIPTION_CTA_BASE_URL } from "../../types/event.types";
import { DEFAULT_TIMEZONE, US_TIMEZONES } from "../common/TimezoneSelector";

// Reward type labels
const REWARD_TYPE_LABELS: Record<RewardType, { label: string; color: string }> =
  {
    none: { label: "No Reward", color: "gray" },
    voucher: { label: "Voucher", color: "green" },
    offer: { label: "Offer", color: "yellow" },
    gift: { label: "Gift", color: "purple" },
    other: { label: "Other", color: "blue" },
  };
import Card from "../ui/Card";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import ConfirmDialog from "../ui/ConfirmDialog";
import CloneCTAForRepModal from "./CloneCTAForRepModal";
import CTAVoucherScopeSelector from "./CTAVoucherScopeSelector";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ceilingProblem,
  getEventStateOffer,
} from "../../services/eventCTAInit.service";
import { useAuth } from "../../contexts/AuthContext";
import {
  canManageEventFiles,
  errorMessage,
  flyerEligibility,
  flyerLabel,
  isPreCutoffEvent,
  LEGACY_STALE_URL_REASON,
  saveCTAFlyerToEvent,
} from "../../services/eventCTAFlyer.service";

interface CTACardProps {
  cta: EventCallToAction;
  event: Event;
  canEdit: boolean;
  onUpdate: () => void;
  /**
   * True for the event's PREPARED CTA: the database derives utm_term from the event's state on every
   * save (migration 006 guard), so the field is shown read-only instead of accepting an edit the
   * server would silently replace (round-2 audit usability note).
   */
  utmTermDerived?: boolean;
}

// Subscription CTA field definitions
const SUBSCRIPTION_FIELDS = [
  { key: "affid", label: "Promoter Affiliate ID", placeholder: "e.g., ABC123" },
  { key: "promo", label: "Promo Code", placeholder: "e.g., SUMMER2025" },
  { key: "altprice", label: "Alt Price", placeholder: "e.g., 10.99" },
  { key: "utm_source", label: "UTM Source", placeholder: "e.g., event" },
  { key: "utm_term", label: "UTM Term", placeholder: "e.g., signup" },
  {
    key: "utm_campaign",
    label: "UTM Campaign",
    placeholder: "e.g., spring_promo",
  },
  { key: "utm_medium", label: "UTM Medium", placeholder: "e.g., qrcode" },
  { key: "utm_content", label: "UTM Content", placeholder: "e.g., banner_ad" },
];

const CTACard: React.FC<CTACardProps> = ({
  cta,
  event,
  canEdit,
  onUpdate,
  utmTermDerived = false,
}) => {
  const { showToast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // R2B (2026-05-17): clone-for-another-rep modal
  const [showCloneModal, setShowCloneModal] = useState(false);

  // Form state
  const [name, setName] = useState(cta.name);
  const [baseUrl, setBaseUrl] = useState(cta.base_url);
  const [parameters, setParameters] = useState<Record<string, string>>(
    cta.parameters,
  );

  // Reward state
  const [rewardType, setRewardType] = useState<RewardType>(
    cta.reward_type || "none",
  );
  const [rewardDescription, setRewardDescription] = useState(
    cta.reward_description || "",
  );
  const [voucherAmount, setVoucherAmount] = useState(
    cta.voucher_amount?.toString() || "",
  );
  const [voucherStartDate, setVoucherStartDate] = useState(
    cta.voucher_start_date || "",
  );
  const [voucherEndDate, setVoucherEndDate] = useState(
    cta.voucher_end_date || "",
  );
  const [voucherExpiryDays, setVoucherExpiryDays] = useState(
    cta.voucher_expiry_days?.toString() || "30",
  );
  const [voucherExpiresAtEventEnd, setVoucherExpiresAtEventEnd] = useState(
    cta.voucher_expires_at_event_end ?? false,
  );

  // URL configuration
  const [includeStateParam, setIncludeStateParam] = useState(
    cta.include_state_param !== false,
  );
  // `=== true`, not `!== false`: defaults OFF, so a CTA saved before this option existed reads
  // as unchecked and keeps its current URL.
  const [useStateDefaults, setUseStateDefaults] = useState(
    cta.use_state_defaults === true,
  );
  // Off by default (owner 2026-09-29); `=== true` so a CTA saved before this option existed reads unchecked.
  const [singleVoucher, setSingleVoucher] = useState(
    cta.single_voucher === true,
  );
  const [useMemberSignup, setUseMemberSignup] = useState(
    cta.use_member_signup || false,
  );
  const [displayAwayPlans, setDisplayAwayPlans] = useState(
    cta.display_away_plans || false,
  );

  // For 'other' type: dynamic parameter management
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");

  // Mutations
  const updateCTA = useUpdateEventCTA();
  const deleteCTA = useDeleteEventCTA();
  const generateQR = useGenerateCTAQRCode();

  // Reset form when CTA changes
  useEffect(() => {
    setName(cta.name);
    setBaseUrl(cta.base_url);
    setParameters(cta.parameters);
    setRewardType(cta.reward_type || "none");
    setRewardDescription(cta.reward_description || "");
    setVoucherAmount(cta.voucher_amount?.toString() || "");
    setVoucherStartDate(cta.voucher_start_date || "");
    setVoucherEndDate(cta.voucher_end_date || "");
    setVoucherExpiryDays(cta.voucher_expiry_days?.toString() || "30");
    setVoucherExpiresAtEventEnd(cta.voucher_expires_at_event_end ?? false);
    setIncludeStateParam(cta.include_state_param !== false);
    setUseStateDefaults(cta.use_state_defaults === true);
    setSingleVoucher(cta.single_voucher === true);
    setUseMemberSignup(cta.use_member_signup || false);
    setDisplayAwayPlans(cta.display_away_plans || false);
  }, [cta]);

  // Insurance-state gun show / range night links go to the classroom page; the server decides (migration 410).
  const {
    data: ctaBaseVariant,
    isPending: variantPending,
    isError: variantError,
  } = useQuery({
    queryKey: [
      "event-cta-base-variant",
      event.id,
      event.type,
      event.location_state,
    ],
    queryFn: () => getEventCTABaseVariant(event.id),
    enabled: cta.cta_type === "subscription",
  });
  // Mig 1480: a gun show / range night CTA may give at most the state's Event in a Box offer.
  const eibOfferType =
    event.type === "gun_show" || event.type === "range_night";
  const { data: stateOffer } = useQuery({
    queryKey: [
      "event-state-offer",
      event.id,
      event.type,
      event.location_state,
      event.state,
    ],
    queryFn: () => getEventStateOffer(event.id),
    enabled: eibOfferType && cta.cta_type === "subscription",
    staleTime: 60_000,
  });
  const stateCeiling =
    eibOfferType &&
    stateOffer?.offer_status === "voucher" &&
    Number(stateOffer.ceiling) > 0
      ? Number(stateOffer.ceiling)
      : null;

  // Insurance-state venue: no discountvalue (gun show / range night: classroom page too) -- migration 410 rev 2.
  const omitDiscount = ctaOmitsDiscount(
    { cta_type: cta.cta_type, use_member_signup: useMemberSignup },
    ctaBaseVariant,
  );

  // Build preview URL (include CTA id and voucher amount for auto-populated params)
  const previewUrl = buildCTAUrl(
    {
      id: cta.id,
      cta_type: cta.cta_type,
      base_url: baseUrl,
      parameters,
      voucher_amount: voucherAmount
        ? parseFloat(voucherAmount)
        : cta.voucher_amount,
      include_state_param: includeStateParam,
      use_state_defaults: useStateDefaults,
      single_voucher: singleVoucher,
      reward_type: rewardType,
      use_member_signup: useMemberSignup,
      display_away_plans: displayAwayPlans,
    },
    { ...event, cta_base_variant: ctaBaseVariant ?? "standard" },
  );

  const handleSave = async () => {
    if (!name.trim()) {
      showToast("error", "Please enter a name");
      return;
    }

    if (cta.cta_type === "other" && !baseUrl.trim()) {
      showToast("error", "Please enter a base URL");
      return;
    }

    // Validate voucher amount if reward type is voucher
    if (
      rewardType === "voucher" &&
      (!voucherAmount || parseFloat(voucherAmount) <= 0)
    ) {
      showToast("error", "Please enter a valid voucher amount");
      return;
    }
    // Mig 1480: never above the state's Event in a Box offer (lowering, or keeping a legacy amount, is fine).
    if (rewardType === "voucher" && cta.cta_type === "subscription") {
      const overCeiling = ceilingProblem(
        stateOffer,
        parseFloat(voucherAmount),
        cta.reward_type === "voucher" ? cta.voucher_amount : null,
      );
      if (overCeiling) {
        showToast("error", overCeiling);
        return;
      }
    }

    // Sprint A Batch 3 Ship B (R2 auditor-recommended): warn when
    // flipping an existing voucher CTA to attribution-only, since this
    // clears the voucher amount / dates that were previously set.
    if (cta.reward_type === "voucher" && rewardType === "none") {
      if (
        !window.confirm(
          "Changing this CTA from Voucher to None will clear the voucher amount and dates. Continue?",
        )
      ) {
        return;
      }
    }

    // Post-deploy audit follow-up (Batch 3 should-fix): same guard for
    // offer-linked CTAs flipped away from offer — the offer_id will be
    // cleared when reward_type leaves "offer".
    if (cta.reward_type === "offer" && rewardType !== "offer" && cta.offer_id) {
      if (
        !window.confirm(
          "Changing this CTA away from Offer will unlink it from the current offer. Continue?",
        )
      ) {
        return;
      }
    }

    try {
      await updateCTA.mutateAsync({
        ctaId: cta.id,
        updates: {
          name: name.trim(),
          base_url:
            cta.cta_type === "subscription"
              ? SUBSCRIPTION_CTA_BASE_URL
              : baseUrl.trim(),
          parameters,
          reward_type: rewardType,
          reward_description: rewardDescription.trim() || null,
          // Post-deploy audit follow-up: preserve offer_id when type
          // stays "offer"; null it otherwise so flips don't leave a
          // stale linkage.
          offer_id: rewardType === "offer" ? (cta.offer_id ?? null) : null,
          voucher_amount:
            rewardType === "voucher" && voucherAmount
              ? parseFloat(voucherAmount)
              : null,
          voucher_start_date:
            rewardType === "voucher" && voucherStartDate
              ? voucherStartDate
              : null,
          voucher_end_date:
            rewardType === "voucher" && voucherEndDate ? voucherEndDate : null,
          // R2 (Ship B should-fix): explicit null instead of undefined so
          // edit paths reliably clear the field. Column is nullable on PROD.
          voucher_expiry_days:
            rewardType === "voucher" &&
            !voucherExpiresAtEventEnd &&
            voucherExpiryDays
              ? parseInt(voucherExpiryDays, 10)
              : null,
          voucher_expires_at_event_end:
            rewardType === "voucher" ? voucherExpiresAtEventEnd : false,
          include_state_param:
            cta.cta_type === "subscription" ? includeStateParam : undefined,
          use_state_defaults:
            cta.cta_type === "subscription" ? useStateDefaults : undefined,
          single_voucher:
            cta.cta_type === "subscription" ? singleVoucher : undefined,
          use_member_signup:
            cta.cta_type === "subscription" ? useMemberSignup : undefined,
          display_away_plans:
            cta.cta_type === "subscription" ? displayAwayPlans : undefined,
        },
      });
      showToast("success", "CTA updated");
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update CTA";
      showToast("error", message);
    }
  };

  const handleCancel = () => {
    setName(cta.name);
    setBaseUrl(cta.base_url);
    setParameters(cta.parameters);
    setRewardType(cta.reward_type || "none");
    setRewardDescription(cta.reward_description || "");
    setVoucherAmount(cta.voucher_amount?.toString() || "");
    setVoucherStartDate(cta.voucher_start_date || "");
    setVoucherEndDate(cta.voucher_end_date || "");
    setVoucherExpiryDays(cta.voucher_expiry_days?.toString() || "30");
    setVoucherExpiresAtEventEnd(cta.voucher_expires_at_event_end ?? false);
    setIncludeStateParam(cta.include_state_param !== false);
    setUseStateDefaults(cta.use_state_defaults === true);
    setSingleVoucher(cta.single_voucher === true);
    setUseMemberSignup(cta.use_member_signup || false);
    setDisplayAwayPlans(cta.display_away_plans || false);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    try {
      await deleteCTA.mutateAsync({ ctaId: cta.id, eventId: cta.event_id });
      showToast("success", "CTA deleted");
      setShowDeleteConfirm(false);
      onUpdate();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete CTA";
      showToast("error", message);
    }
  };

  const handleGenerateQR = async () => {
    // Validate that we have enough parameters
    if (cta.cta_type === "subscription" && !event.state) {
      showToast("error", "Event must have a regulatory state set");
      return;
    }

    try {
      await generateQR.mutateAsync({ ctaId: cta.id, event });
      showToast("success", "QR code generated");
      onUpdate();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to generate QR code";
      showToast("error", message);
    }
  };

  // "Scan to join" flyer -> the event's Files. Go-forward only: eligibility refuses ended events and any CTA without a
  // voucher -- except an insurance-state CTA, whose flyer shows no amount (migration 410) and uses the insurance
  // artwork for the event's type; a type without one (not a range night or gun show) is "artwork_pending". "button"
  // also admits a pre-cutoff event whose generic CTA is already a complete current-rule CTA (owner, 2026-10-12; see
  // isCompleteCurrentRuleGenericCTA).
  const queryClient = useQueryClient();
  const [creatingFlyer, setCreatingFlyer] = useState(false);
  const { user } = useAuth();
  const flyer = flyerEligibility(cta, event, {
    mode: "button",
    baseVariant: ctaBaseVariant,
  });
  const flyerApplies =
    flyer.ok ||
    flyer.kind === "amount" ||
    flyer.kind === "no_qr" ||
    flyer.kind === "legacy_stale_url" ||
    flyer.kind === "artwork_pending";
  // External audit P2 (35406751): on an older event the "QR code is out of date" warning (saved URL differs
  // from the URL shown, including while parameters are being edited) and an enabled Create flyer must never
  // coexist -- Create flyer there only renders the saved QR, it never regenerates it.
  const legacyPreviewStale =
    isPreCutoffEvent(event) &&
    !!cta.generated_url &&
    cta.generated_url !== previewUrl;
  // Files access is narrower than CTA editing (admin/manager/event owner). Checked up front so a user
  // who can't save files is told who can, instead of being offered a retry that can't work (audit M2).
  const { data: canFiles } = useQuery({
    queryKey: ["can-manage-event-files", event.id],
    queryFn: () => canManageEventFiles(event.id),
    enabled: flyerApplies,
  });
  let flyerReason: string | null = null;
  // Internal audit P3: until the venue variant is known (or if it could not be read) the card cannot tell an
  // insurance-state CTA from a standard one -- say so instead of a misleading reason (the save path re-decides anyway).
  const variantUnknown =
    cta.cta_type === "subscription" && (variantPending || variantError);
  if (variantUnknown) {
    flyerReason = variantError
      ? "Couldn't check whether this event's venue is in an insurance state. Reload the page to try again."
      : "Checking this event's venue…";
  } else if (!flyer.ok) {
    flyerReason = flyer.reason;
  } else if (legacyPreviewStale) {
    flyerReason = LEGACY_STALE_URL_REASON;
  } else if (canFiles === false) {
    flyerReason =
      "Saving a flyer needs access to this event's Files (admins, managers, or teammates it is shared with).";
  }
  const handleCreateFlyer = async () => {
    if (!user?.id) {
      return;
    }
    setCreatingFlyer(true);
    try {
      await saveCTAFlyerToEvent({
        cta,
        event,
        label: flyerLabel(cta),
        userId: user.id,
      });
      onUpdate();
      await queryClient.invalidateQueries({
        queryKey: ["event-attachments", event.id],
      });
      showToast("success", "Flyer saved to the event's Files.");
    } catch (error) {
      showToast("error", errorMessage(error));
    } finally {
      setCreatingFlyer(false);
    }
  };

  const handleCopyUrl = async () => {
    const urlToCopy = previewUrl;
    try {
      await navigator.clipboard.writeText(urlToCopy);
      showToast("success", "URL copied to clipboard");
    } catch {
      showToast("error", "Failed to copy URL");
    }
  };

  const handleDownloadQR = async () => {
    if (!cta.qr_code_url) {
      return;
    }

    try {
      // Fetch the QR code image
      const response = await fetch(cta.qr_code_url);
      const blob = await response.blob();

      // Filename: {event name}.png. Per 2026-05-18 operator feedback,
      // dropped cta.name from the pattern — operators name CTAs with
      // internal labels (e.g. "Test 8532") that shouldn't leak into
      // user-facing download filenames.
      const eventName = (event.title || "event").replace(/[^a-zA-Z0-9]+/g, "-");
      const filename = `${eventName}.png`;

      // If it's an SVG, convert to PNG
      if (blob.type === "image/svg+xml" || cta.qr_code_url.endsWith(".svg")) {
        const svgText = await blob.text();
        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            // Set canvas size (use larger size for better quality)
            canvas.width = 512;
            canvas.height = 512;
            if (ctx) {
              // White background
              ctx.fillStyle = "white";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            }
            resolve();
          };
          img.onerror = reject;
          // UTF-8-safe: btoa() only handles Latin-1 and throws on any code
          // point > 255 (e.g. the "…" ellipsis truncate() adds for labels
          // longer than 32 chars, or smart quotes/accents in event/rep names).
          // Use a charset=utf-8 data URI instead of base64.
          img.src =
            "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgText);
        });

        canvas.toBlob((pngBlob) => {
          if (pngBlob) {
            const url = URL.createObjectURL(pngBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }, "image/png");
      } else {
        // Already PNG or other format, download directly
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Failed to download QR code:", error);
      showToast("error", "Failed to download QR code");
    }
  };

  // Add a new parameter for 'other' type
  const handleAddParameter = () => {
    if (!newParamKey.trim()) {
      showToast("error", "Enter a parameter name");
      return;
    }
    setParameters((prev) => ({
      ...prev,
      [newParamKey.trim()]: newParamValue,
    }));
    setNewParamKey("");
    setNewParamValue("");
  };

  // Remove a parameter
  const handleRemoveParameter = (key: string) => {
    setParameters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  // Update a parameter value
  const handleParameterChange = (key: string, value: string) => {
    setParameters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <>
      <Card variant="outlined" padding="none" className="overflow-hidden">
        {/* Header */}
        <div
          className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center space-x-3">
            <QrCodeIcon className="h-5 w-5 text-gray-400" />
            <div>
              <h4 className="font-medium text-gray-900">{cta.name}</h4>
              <p className="text-xs text-gray-500 capitalize">
                {cta.cta_type === "subscription"
                  ? "Subscription CTA"
                  : cta.cta_type === "registration"
                    ? "Registration CTA"
                    : "Custom CTA"}
                {cta.sales_position && (
                  <span className="text-gray-400 ml-1">
                    ·{" "}
                    {cta.sales_position.assigned_name ||
                      cta.sales_position.title}
                    {cta.sales_position.is_active === false
                      ? " (inactive)"
                      : ""}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Phase 1.1 Ship 3 — distinct visual marker for the
                auto-minted "Generic — no rep" CTA. Renders even when
                reward_type is "none" (which is the default for
                generic CTAs). */}
            {cta.is_system_generic && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                title="Auto-generated fallback CTA used when no rep is attributed"
              >
                Generic
              </span>
            )}
            {/* Phase 1.1 Ship 3 — QR-pending notice (auditor Q5 yes).
                Shows on generic CTAs that don't yet have both URLs. */}
            {/* Any CTA without a saved URL + QR (round-4 audit: a state change now clears rep/manual
                CTAs too, so the badge can't be limited to the generic one). */}
            {(!cta.generated_url || !cta.qr_code_url) && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200"
                title="This CTA has no current QR code (never generated, or cleared because the CTA or the event's state changed). Open it and click 'Generate URL & QR'."
              >
                QR pending
              </span>
            )}
            {cta.reward_type && cta.reward_type !== "none" && (
              <span
                className={`
                inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                ${cta.reward_type === "voucher" ? "bg-green-100 text-green-800" : ""}
                ${cta.reward_type === "gift" ? "bg-purple-100 text-purple-800" : ""}
                ${cta.reward_type === "other" ? "bg-blue-100 text-blue-800" : ""}
              `}
              >
                {REWARD_TYPE_LABELS[cta.reward_type]?.label}
                {cta.reward_type === "voucher" &&
                  cta.voucher_amount &&
                  ` ($${cta.voucher_amount})`}
              </span>
            )}
            {cta.qr_code_url && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                QR Ready
              </span>
            )}
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            {/* Name Field */}
            {isEditing ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            ) : null}

            {/* Parameters Section */}
            {cta.cta_type === "subscription" ? (
              // Subscription CTA Fields
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700">
                  Parameters
                </h5>

                {/* Auto-populated fields info */}
                <div className="bg-blue-50 rounded-md p-3 text-sm text-blue-700">
                  <p className="font-medium mb-1">Auto-populated:</p>
                  <ul className="list-disc list-inside text-xs space-y-0.5">
                    <li
                      className={
                        !includeStateParam ? "line-through opacity-50" : ""
                      }
                    >
                      <code className="bg-blue-100 px-1 rounded">ste</code>:{" "}
                      {event.state || "(not set)"}
                    </li>
                    <li>
                      <code className="bg-blue-100 px-1 rounded">ctaid</code>:{" "}
                      {cta.id.substring(0, 8)}...
                    </li>
                    <li>
                      <code className="bg-blue-100 px-1 rounded">eventid</code>:{" "}
                      {event.id.substring(0, 8)}...
                    </li>
                    {omitDiscount ? (
                      <li className="text-gray-600">
                        <code className="bg-blue-100 px-1 rounded line-through">
                          discountvalue
                        </code>{" "}
                        not sent: this event's venue is in an insurance state,
                        so its link carries no discount amount
                      </li>
                    ) : (
                      <li>
                        <code className="bg-blue-100 px-1 rounded">
                          discountvalue
                        </code>
                        :{" "}
                        {voucherAmount
                          ? voucherAmount
                          : cta.voucher_amount
                            ? cta.voucher_amount
                            : "(set voucher amount)"}
                      </li>
                    )}
                  </ul>
                </div>

                {/* Include state parameter checkbox */}
                {isEditing && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeStateParam}
                        onChange={(e) => setIncludeStateParam(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Include state parameter (
                      <code className="text-xs bg-gray-100 px-1 rounded">
                        ste=
                      </code>
                      ) in URL
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={useStateDefaults}
                        onChange={(e) => setUseStateDefaults(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        Use State Defaults
                        <span className="block text-xs text-gray-500">
                          If the member picks a different state during signup,
                          the voucher amount switches to that state's default.{" "}
                          Adds{" "}
                          <code className="bg-gray-100 px-1 rounded">
                            usestatedefaults=1
                          </code>{" "}
                          to the URL. Off by default, so existing CTAs and
                          printed QR codes are unchanged.
                        </span>
                      </span>
                    </label>

                    {(rewardType === "voucher" || rewardType === "offer") && (
                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          aria-label="Single voucher"
                          checked={singleVoucher}
                          onChange={(e) => setSingleVoucher(e.target.checked)}
                          className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span>
                          Single voucher
                          <span className="block text-xs text-gray-500">
                            Creates the voucher for the primary member only, not
                            the secondary. Adds{" "}
                            <code className="bg-gray-100 px-1 rounded">
                              singlevoucher=1
                            </code>{" "}
                            to the QR code and landing page URL. Off by default.
                          </span>
                        </span>
                      </label>
                    )}

                    {/* Use member signup URL checkbox */}
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useMemberSignup}
                        onChange={(e) => setUseMemberSignup(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Use member signup URL
                      <span className="text-xs text-gray-500">
                        Skips the voucher landing page and starts on plan
                        selection (unchecked = /voucher/, checked =
                        /member-signup/)
                      </span>
                    </label>

                    {/* Display away plans checkbox */}
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={displayAwayPlans}
                        onChange={(e) => setDisplayAwayPlans(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Display away plans
                      <span className="text-xs text-gray-500">
                        (with member signup URL, starts on the Away plans
                        screen; adds plantype=away)
                      </span>
                    </label>
                  </>
                )}

                {/* Editable fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {SUBSCRIPTION_FIELDS.map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {field.label}
                      </label>
                      <input
                        type="text"
                        value={parameters[field.key] || ""}
                        onChange={(e) =>
                          handleParameterChange(field.key, e.target.value)
                        }
                        placeholder={field.placeholder}
                        disabled={
                          !isEditing ||
                          (utmTermDerived && field.key === "utm_term")
                        }
                        className={`
                          w-full px-3 py-2 border rounded-md text-sm
                          ${
                            isEditing &&
                            !(utmTermDerived && field.key === "utm_term")
                              ? "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                              : "border-gray-200 bg-gray-50 text-gray-600"
                          }
                        `}
                      />
                      {utmTermDerived && field.key === "utm_term" && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Always the event&apos;s state for this CTA.
                        </p>
                      )}
                      {field.key === "promo" && (
                        // Informational only -- never blocks the save (owner rule 2026-10-25).
                        <PromoCodeLookupStatus
                          code={parameters.promo}
                          canRecheck={canEdit}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Other CTA Fields
              <div className="space-y-3">
                {/* Base URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base URL
                  </label>
                  <input
                    type="url"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder="https://example.com/landing"
                    disabled={!isEditing}
                    className={`
                      w-full px-3 py-2 border rounded-md
                      ${
                        isEditing
                          ? "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      }
                    `}
                  />
                </div>

                {/* Dynamic Parameters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Parameters
                  </label>

                  {Object.keys(parameters).length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {Object.entries(parameters).map(([key, value]) => (
                        <div key={key} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={key}
                            disabled
                            className="w-1/3 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm"
                          />
                          <span className="text-gray-400">=</span>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) =>
                              handleParameterChange(key, e.target.value)
                            }
                            disabled={!isEditing}
                            className={`
                              flex-1 px-3 py-2 border rounded-md text-sm
                              ${
                                isEditing
                                  ? "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                                  : "border-gray-200 bg-gray-50"
                              }
                            `}
                          />
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleRemoveParameter(key)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-3">
                      No parameters added yet
                    </p>
                  )}

                  {/* Add new parameter */}
                  {isEditing && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newParamKey}
                        onChange={(e) => setNewParamKey(e.target.value)}
                        placeholder="param_name"
                        className="w-1/3 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                      <span className="text-gray-400">=</span>
                      <input
                        type="text"
                        value={newParamValue}
                        onChange={(e) => setNewParamValue(e.target.value)}
                        placeholder="value"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddParameter}
                        className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reward Configuration */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h5 className="text-sm font-medium text-gray-700">
                Reward Configuration
              </h5>

              {isEditing ? (
                <>
                  {/* Reward Type Selector (editing mode) */}
                  {omitDiscount && (
                    <p
                      className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
                      data-testid="cta-no-voucher-note"
                    >
                      {INSURANCE_NO_VOUCHER_NOTE}
                    </p>
                  )}
                  <div className="grid grid-cols-5 gap-2">
                    {(
                      [
                        "none",
                        "voucher",
                        "offer",
                        "gift",
                        "other",
                      ] as RewardType[]
                    ).map((type) => (
                      <button
                        key={type}
                        type="button"
                        disabled={omitDiscount && type === "voucher"}
                        title={
                          omitDiscount && type === "voucher"
                            ? INSURANCE_NO_VOUCHER_NOTE
                            : undefined
                        }
                        onClick={() => setRewardType(type)}
                        className={`
                          p-2 border rounded text-center text-sm transition-colors
                          ${
                            rewardType === type
                              ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                              : "border-gray-300 hover:border-gray-400"
                          }
                        `}
                      >
                        {REWARD_TYPE_LABELS[type].label}
                      </button>
                    ))}
                  </div>

                  {/* Voucher fields (editing mode) */}
                  {rewardType === "voucher" && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 space-y-3">
                      <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          checked={voucherExpiresAtEventEnd}
                          onChange={(e) =>
                            setVoucherExpiresAtEventEnd(e.target.checked)
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        Expire voucher at the end of the event
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Amount ($) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={voucherAmount}
                            onChange={(e) => setVoucherAmount(e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                          />
                          {cta.cta_type === "subscription" &&
                            stateCeiling !== null && (
                              <p
                                className="text-xs text-gray-500 mt-1"
                                data-testid="cta-edit-state-ceiling"
                              >
                                Up to ${stateCeiling} (the Event in a Box offer
                                for {stateOffer?.state ?? "this state"}). You
                                can lower it, not raise it.
                              </p>
                            )}
                        </div>
                        {!voucherExpiresAtEventEnd && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Expiry Days{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={voucherExpiryDays}
                              onChange={(e) =>
                                setVoucherExpiryDays(e.target.value)
                              }
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                            />
                            <p className="text-xs text-gray-500 mt-0.5">
                              Days from creation until expiry
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Valid From
                          </label>
                          <input
                            type="date"
                            value={voucherStartDate}
                            onChange={(e) =>
                              setVoucherStartDate(e.target.value)
                            }
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Valid Until
                          </label>
                          <input
                            type="date"
                            value={voucherEndDate}
                            onChange={(e) => setVoucherEndDate(e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-green-700 mt-1">
                        Dates use event timezone (
                        {US_TIMEZONES.find(
                          (tz) =>
                            tz.value === (event.timezone || DEFAULT_TIMEZONE),
                        )?.label || "Eastern"}{" "}
                        -{" "}
                        {US_TIMEZONES.find(
                          (tz) =>
                            tz.value === (event.timezone || DEFAULT_TIMEZONE),
                        )?.abbr || "EST"}
                        )
                      </p>
                    </div>
                  )}

                  {/* Offer linkage (editing mode — read-only here) */}
                  {rewardType === "offer" && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-gray-700">
                      This CTA links to an existing offer. To change the linked
                      offer, delete and recreate the CTA from the parent form.
                    </div>
                  )}

                  {/* Reward Description (editing mode) */}
                  {(rewardType === "gift" || rewardType === "other") && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={rewardDescription}
                        onChange={(e) => setRewardDescription(e.target.value)}
                        placeholder={
                          rewardType === "gift"
                            ? "e.g., Free T-shirt"
                            : "Describe the reward..."
                        }
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}
                </>
              ) : (
                /* View mode */
                <div className="text-sm">
                  {cta.reward_type === "none" || !cta.reward_type ? (
                    <p className="text-gray-500">
                      No reward configured (tracking only)
                    </p>
                  ) : cta.reward_type === "voucher" ? (
                    <div className="space-y-1">
                      <p className="text-green-700 font-medium">
                        Voucher: ${cta.voucher_amount?.toFixed(2)}
                      </p>
                      <p className="text-gray-500 text-xs">
                        {cta.voucher_expires_at_event_end
                          ? "Expires at the end of the event"
                          : `Expires ${cta.voucher_expiry_days || 30} days after creation`}
                      </p>
                      {(cta.voucher_start_date || cta.voucher_end_date) && (
                        <p className="text-gray-500 text-xs">
                          Valid: {cta.voucher_start_date || "Any"} →{" "}
                          {cta.voucher_end_date || "No expiry"}
                        </p>
                      )}
                    </div>
                  ) : cta.reward_type === "offer" ? (
                    <div className="space-y-1">
                      <p className="text-yellow-700 font-medium">
                        Offer-linked
                      </p>
                      {cta.offer_id && (
                        <p className="text-gray-500 text-xs font-mono">
                          {cta.offer_id}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p
                        className={`font-medium ${cta.reward_type === "gift" ? "text-purple-700" : "text-blue-700"}`}
                      >
                        {REWARD_TYPE_LABELS[cta.reward_type]?.label}
                      </p>
                      {cta.reward_description && (
                        <p className="text-gray-600">
                          {cta.reward_description}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Where the voucher can be used (migration 1540; the server decides when it applies) */}
            <CTAVoucherScopeSelector
              ctaId={cta.id}
              rewardType={cta.reward_type}
              voucherEndDate={cta.voucher_end_date}
              expiresAtEventEnd={cta.voucher_expires_at_event_end}
              eventState={event.location_state}
              eventType={event.type}
              venueName={event.location_name}
            />

            {/* Generated URL Preview */}
            <div className="bg-gray-50 rounded-md p-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {cta.generated_url ? "Generated URL" : "URL Preview"}
                </label>
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                >
                  <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  <span>Copy</span>
                </button>
              </div>
              <p className="text-sm text-gray-700 break-all font-mono">
                {previewUrl}
              </p>
              {cta.generated_url && cta.generated_url !== previewUrl && (
                <p className="text-xs text-amber-600 mt-1">
                  Event details have changed since the QR code was generated.
                  Regenerate to update.
                </p>
              )}
            </div>

            {/* QR Code Section */}
            {cta.qr_code_url && (
              <div className="flex items-start space-x-4 p-4 bg-white border border-gray-200 rounded-lg">
                <div className="bg-white p-2 border border-gray-200 rounded-lg">
                  <img
                    src={cta.qr_code_url}
                    alt={`QR code for ${cta.name}`}
                    className="w-40 max-h-48 object-contain"
                  />
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900 mb-1">QR Code</h5>
                  <p className="text-sm text-gray-500 mb-3">
                    Scan to open the generated URL
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={ArrowDownTrayIcon}
                    onClick={handleDownloadQR}
                  >
                    Download SVG
                  </Button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {canEdit && (
              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <div>
                  {isEditing ? (
                    <div className="flex space-x-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={updateCTA.isPending}
                      >
                        {updateCTA.isPending ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit Parameters
                    </Button>
                  )}
                </div>

                <div className="flex flex-col items-end">
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={cta.qr_code_url ? ArrowPathIcon : QrCodeIcon}
                      onClick={handleGenerateQR}
                      disabled={generateQR.isPending}
                    >
                      {generateQR.isPending
                        ? "Generating..."
                        : cta.qr_code_url
                          ? "Regenerate URL & QR"
                          : "Generate URL & QR"}
                    </Button>
                    {flyerApplies && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleCreateFlyer}
                        disabled={!!flyerReason || creatingFlyer}
                        title="Put this CTA's QR code on the 'Scan to join' flyer and save it to the event's Files"
                      >
                        <PhotoIcon className="h-4 w-4 mr-1" />
                        {creatingFlyer ? "Creating flyer..." : "Create flyer"}
                      </Button>
                    )}
                    {/* R2B (2026-05-17): Clone for another rep button.
                      Opens a small modal that asks which rep to assign
                      to the clone, then creates a duplicate CTA with
                      that rep's attribution. */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowCloneModal(true)}
                    >
                      + Clone for another rep
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      icon={TrashIcon}
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete
                    </Button>
                  </div>
                  {flyerApplies && flyerReason && (
                    <p className="text-xs text-gray-500 mt-1">
                      Flyer: {flyerReason}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Call to Action"
        message={`Are you sure you want to delete "${cta.name}"? This will also delete any associated QR codes. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteCTA.isPending}
      />

      {/* R2B (2026-05-17): Clone-for-another-rep modal */}
      {showCloneModal && (
        <CloneCTAForRepModal
          sourceCTA={cta}
          eventId={event.id}
          event={event}
          onClose={() => setShowCloneModal(false)}
          onCloned={() => {
            setShowCloneModal(false);
            onUpdate();
          }}
        />
      )}
    </>
  );
};

export default CTACard;
